import {
    ArrowTrendingUpIcon,
    CubeIcon,
    CurrencyDollarIcon,
    ShoppingBagIcon,
    UsersIcon,
} from "@heroicons/react/24/outline";
import { useCallback, useEffect, useState } from "react";
import { ADMIN_CONFIG } from "../../config/adminConfig";
import { useAdmin } from "../../contexts/AdminContext";
import { adminService } from "../../services/api";

const DashboardStats = () => {
    const { refreshDashboard, refreshKey } = useAdmin();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [period, setPeriod] = useState(ADMIN_CONFIG.defaultPeriod);

    const fetchDashboardStats = useCallback(async () => {
        try {
            setLoading(true);
            const response = await adminService.getDashboardStats(period);
            setStats(response.data);
        } catch (error) {
            console.error("Failed to fetch dashboard stats:", error);
        } finally {
            setLoading(false);
        }
    }, [period]);

    useEffect(() => {
        fetchDashboardStats();
    }, [fetchDashboardStats, refreshKey]);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-green-200 border-t-[#008000]"></div>
            </div>
        );
    }

    if (!stats) {
        return (
            <div className="text-center py-12">
                <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                    </svg>
                </div>
                <p className="text-gray-500 font-medium">Failed to load dashboard statistics</p>
                <p className="text-gray-400 text-sm mt-1">Please refresh the page or try again later</p>
            </div>
        );
    }

    const statCards = [
        {
            title: "Total Users",
            value: stats.overview.users.total,
            change: `+${stats.overview.users.new || 0} new this period`,
            trend: "up",
            icon: UsersIcon,
            gradient: "from-blue-500 to-cyan-500",
            bgGradient: "from-blue-50 to-cyan-50",
        },
        {
            title: "Orders This Period",
            value: stats.overview.orders.new || 0,
            change: `${stats.overview.orders.total || 0} total all-time`,
            trend: "up",
            icon: ShoppingBagIcon,
            gradient: "from-green-500 to-emerald-500",
            bgGradient: "from-green-50 to-emerald-50",
        },
        {
            title: "Revenue This Period",
            value: `₹${(stats.overview.revenue.period || 0).toLocaleString()}`,
            change: `₹${(stats.overview.revenue.total || 0).toLocaleString()} total all-time`,
            trend: "up",
            icon: CurrencyDollarIcon,
            gradient: "from-yellow-500 to-orange-500",
            bgGradient: "from-yellow-50 to-orange-50",
        },
        {
            title: "Active Products",
            value: stats.overview.products.active || 0,
            change: `${stats.overview.products.total || 0} total`,
            trend: "neutral",
            icon: CubeIcon,
            gradient: "from-purple-500 to-violet-500",
            bgGradient: "from-purple-50 to-violet-50",
        },
    ];

    return (
        <div className="space-y-6 sm:space-y-8">
            {/* Period Selector */}
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-4 sm:space-y-0">
                <div>
                    <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                        Analytics Overview
                    </h2>
                    <p className="text-sm sm:text-base text-gray-600 mt-1">
                        Track your business performance and insights
                    </p>
                </div>

                <select
                    value={period}
                    onChange={e => setPeriod(e.target.value)}
                    className="bg-white border border-gray-200 rounded-xl px-4 py-2.5 text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent shadow-sm hover:shadow-md transition-shadow w-full sm:w-auto"
                >
                    {ADMIN_CONFIG.timePeriods.map(periodOption => (
                        <option key={periodOption.value} value={periodOption.value}>
                            {periodOption.label}
                        </option>
                    ))}
                </select>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 sm:gap-6">
                {statCards.map((stat, index) => {
                    const Icon = stat.icon;
                    return (
                        <div
                            key={index}
                            className={`relative overflow-hidden bg-gradient-to-br ${stat.bgGradient} rounded-2xl p-4 sm:p-6 border border-white/50 shadow-lg hover:shadow-xl transition-shadow`}
                        >
                            <div className="relative">
                                <div className="flex items-start justify-between mb-3 sm:mb-4">
                                    <div
                                        className={`p-2.5 sm:p-3 rounded-xl sm:rounded-2xl bg-gradient-to-br ${stat.gradient} shadow-lg`}
                                    >
                                        <Icon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                                    </div>

                                    {stat.trend === "up" && (
                                        <div className="flex items-center space-x-1 text-[#008000]">
                                            <ArrowTrendingUpIcon className="h-3 w-3 sm:h-4 sm:w-4" />
                                            <span className="text-xs font-medium hidden sm:inline">Trending</span>
                                        </div>
                                    )}
                                </div>

                                <div>
                                    <p className="text-xs sm:text-sm font-medium text-gray-600 mb-1">{stat.title}</p>
                                    <p className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-1 sm:mb-2">
                                        {stat.value}
                                    </p>
                                    <p className="text-xs sm:text-sm text-gray-500">{stat.change}</p>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Charts Section */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 sm:gap-8">
                {/* Order Status Distribution */}
                <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 sm:p-6 border border-gray-200/50 shadow-lg hover:shadow-xl transition-shadow">
                    <div className="flex items-center justify-between mb-4 sm:mb-6">
                        <h3 className="text-base sm:text-lg lg:text-xl font-bold text-gray-900">Order Status</h3>
                        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    </div>
                    <div className="space-y-3 sm:space-y-4 max-h-64 sm:max-h-none overflow-y-auto">
                        {stats.charts.orderStatus && stats.charts.orderStatus.length > 0 ? (
                            stats.charts.orderStatus.map((status, index) => (
                                <div
                                    key={index}
                                    className="flex justify-between items-center p-2.5 sm:p-3 rounded-xl bg-gray-50/50 hover:bg-gray-100/50 transition-colors"
                                >
                                    <span className="text-xs sm:text-sm font-medium text-gray-700 capitalize flex items-center">
                                        <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                                        {status._id ? status._id.replace("_", " ") : "Unknown"}
                                    </span>
                                    <span className="text-xs sm:text-sm font-bold text-gray-900 bg-white px-2 py-1 rounded-lg">
                                        {status.count || 0}
                                    </span>
                                </div>
                            ))
                        ) : (
                            <div className="text-center py-8 text-gray-500 text-sm">
                                No orders in this period
                            </div>
                        )}
                    </div>
                </div>

                {/* Top Products */}
                <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 sm:p-6 border border-gray-200/50 shadow-lg hover:shadow-xl transition-shadow">
                    <div className="flex items-center justify-between mb-4 sm:mb-6">
                        <h3 className="text-base sm:text-lg lg:text-xl font-bold text-gray-900">Top Products</h3>
                        <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                    </div>
                    <div className="space-y-3 sm:space-y-4 max-h-64 sm:max-h-none overflow-y-auto">
                        {stats.charts.topProducts && stats.charts.topProducts.length > 0 ? (
                            stats.charts.topProducts.map((product, index) => (
                                <div
                                    key={index}
                                    className="flex justify-between items-center p-2.5 sm:p-3 rounded-xl bg-gray-50/50 hover:bg-gray-100/50 transition-colors"
                                >
                                    <div className="min-w-0 flex-1 mr-3">
                                        <p className="text-xs sm:text-sm font-medium text-gray-900 truncate">
                                            {product.name || "Unknown Product"}
                                        </p>
                                        <p className="text-xs text-gray-500 capitalize">{product.category || "Uncategorized"}</p>
                                    </div>
                                    <div className="text-right flex-shrink-0">
                                        <p className="text-xs sm:text-sm font-bold text-gray-900">
                                            {product.totalSold || 0} sold
                                        </p>
                                        <p className="text-xs text-[#008000]">₹{(product.revenue || 0).toLocaleString()}</p>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="text-center py-8 text-gray-500 text-sm">
                                No products sold in this period
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 sm:p-6 border border-gray-200/50 shadow-lg">
                <div className="flex items-center justify-between mb-4 sm:mb-6">
                    <h3 className="text-base sm:text-lg lg:text-xl font-bold text-gray-900">Quick Actions</h3>
                    <div className="flex space-x-1">
                        <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse"></div>
                        <div
                            className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"
                            style={{ animationDelay: "0.2s" }}
                        ></div>
                        <div
                            className="w-2 h-2 bg-red-500 rounded-full animate-pulse"
                            style={{ animationDelay: "0.4s" }}
                        ></div>
                    </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                    <div className="group relative overflow-hidden bg-gradient-to-br from-orange-50 to-red-50 border border-orange-200/50 rounded-2xl p-4 sm:p-6 text-left hover:shadow-lg transition-shadow">
                        <div className="relative">
                            <h4 className="text-sm sm:text-base font-semibold text-gray-900 mb-2">Pending Orders</h4>
                            <p className="text-2xl sm:text-3xl font-bold text-orange-600 mb-1">
                                {stats.overview.orders.pending || 0}
                            </p>
                            <p className="text-xs sm:text-sm text-gray-500">Need immediate attention</p>
                        </div>
                    </div>

                    <div className="group relative overflow-hidden bg-gradient-to-br from-blue-50 to-cyan-50 border border-blue-200/50 rounded-2xl p-4 sm:p-6 text-left hover:shadow-lg transition-shadow">
                        <div className="relative">
                            <h4 className="text-sm sm:text-base font-semibold text-gray-900 mb-2">Active Templates</h4>
                            <p className="text-2xl sm:text-3xl font-bold text-blue-600 mb-1">
                                {stats.overview.templates.active || 0}
                            </p>
                            <p className="text-xs sm:text-sm text-gray-500">Available for customer use</p>
                        </div>
                    </div>

                    <div className="group relative overflow-hidden bg-gradient-to-br from-red-50 to-pink-50 border border-red-200/50 rounded-2xl p-4 sm:p-6 text-left hover:shadow-lg transition-shadow sm:col-span-2 lg:col-span-1">
                        <div className="relative">
                            <h4 className="text-sm sm:text-base font-semibold text-gray-900 mb-2">Low Stock Items</h4>
                            <p className="text-2xl sm:text-3xl font-bold text-red-600 mb-1">
                                {(stats.overview.products.total || 0) - (stats.overview.products.active || 0)}
                            </p>
                            <p className="text-xs sm:text-sm text-gray-500">Need restocking soon</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DashboardStats;
